import React, { useState } from "react";
import {
  Edit,
  ChevronDown,
  ChevronRight,
  BarChart2,
  Server,
  Database,
  Tag,
  AlertTriangle,
  Save,
  X,
  Activity,
  Bell,
  Container,
  Scale,
  ScreenShare,
  ShieldCheck,
  Icon
} from "lucide-react";
import { bucket, layoutListMove } from '@lucide/lab';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

const getIconComponent = (namespace) => {
  if (!namespace) return <Activity className="h-4 w-4 text-gray-800" />;

  const namespaceLower = namespace.toLowerCase();

  if (namespaceLower.includes('lambda')) {
    return <Server className="h-4 w-4 text-[#003366]" />;
  } else if (namespaceLower.includes('rds')) {
    return <Database className="h-4 w-4 text-[#003366]" />;
  } else if (namespaceLower.includes('ecs')) {
    return <Container className="h-4 w-4 text-[#003366]" />;
  } else if (namespaceLower.includes('sqs')) {
    return <Icon iconNode={layoutListMove} className="h-4 w-4 text-[#003366]" />;
  } else if (namespaceLower.includes('cloudfront')) {
    return <ScreenShare className="h-4 w-4 text-[#003366]" />;
  } else if (namespaceLower.includes('applicationelb')) {
    return <Scale className="h-4 w-4 text-[#003366]" />;
  } else if (namespaceLower.includes('s3')) {
    return <Icon iconNode={bucket} className="h-4 w-4 text-[#003366]" />;
  } else if (namespaceLower.includes('wafv2')) {
    return <ShieldCheck className="h-4 w-4 text-[#003366]" />;
  }

  return <Activity className="h-4 w-4 text-gray-800" />;
};

export default function DashboardTable({ dashboards, regions, awsAccounts, onEdit, onDelete }) {
  const [expandedDashboards, setExpandedDashboards] = useState({});
  const [expandedNamespaces, setExpandedNamespaces] = useState({});
  const [expandedMetrics, setExpandedMetrics] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, content: "", x: 0, y: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toggle dashboard expansion
  const toggleDashboardExpansion = (dashboardId) => {
    setExpandedDashboards((prev) => ({
      ...prev,
      [dashboardId]: !prev[dashboardId],
    }));
  };

  // Toggle namespace expansion
  const toggleNamespaceExpansion = (dashboardId, namespaceName) => {
    const key = `${dashboardId}-${namespaceName}`;
    setExpandedNamespaces((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Toggle metric expansion
  const toggleMetricExpansion = (dashboardId, namespaceName, metricName) => {
    const key = `${dashboardId}-${namespaceName}-${metricName}`;
    setExpandedMetrics((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Calculate total alarm count for a dashboard
  const calculateTotalAlarms = (dashboard) => {
    let totalAlarms = 0;
    dashboard.namespaces.forEach(namespace => {
      // Use metricsWithAlarms if available, otherwise fall back to metrics
      const metricsToCheck = namespace.metricsWithAlarms || namespace.metrics || [];
      metricsToCheck.forEach(metric => {
        // Handle both object format (metricsWithAlarms) and string format (metrics)
        if (typeof metric === 'object' && metric.alarmDetails && metric.alarmDetails.length > 0) {
          totalAlarms += metric.alarmDetails.length;
        }
      });
    });
    return totalAlarms;
  };

  // Calculate alarm count for a specific namespace
  const calculateNamespaceAlarms = (namespace) => {
    let namespaceAlarms = 0;
    const metricsToCheck = namespace.metricsWithAlarms || namespace.metrics || [];
    metricsToCheck.forEach(metric => {
      // Handle both object format (metricsWithAlarms) and string format (metrics)
      if (typeof metric === 'object' && metric.alarmDetails && metric.alarmDetails.length > 0) {
        namespaceAlarms += metric.alarmDetails.length;
      }
    });
    return namespaceAlarms;
  };

  // Get metrics count for display
  const getMetricsCount = (namespace) => {
    if (namespace.metricsWithAlarms) {
      return namespace.metricsWithAlarms.length;
    }
    return namespace.metrics ? namespace.metrics.length : 0;
  };

  // Handle delete confirmation
  const handleDeleteConfirmation = (id) => {
    setRecordToDelete(id);
    setOpenDialog(true);
  };

  const confirmDelete = () => {
    setIsSubmitting(true);
    onDelete(recordToDelete);
    setTimeout(() => {
      setOpenDialog(false);
      setRecordToDelete(null);
      setIsSubmitting(false);
    }, 500);
  };

  return (
    <div className="p-6">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-[#003366]/5">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider w-1/5">
                Dashboard Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider w-1/5">
                Namespaces / Metrics
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider w-1/6">
                Account
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider w-1/6">
                Region
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider w-1/6">
                Alarms
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-[#003366] uppercase tracking-wider w-1/6">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {dashboards.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center">
                    <BarChart2 className="h-10 w-10 text-gray-400 mb-2" />
                    <p className="text-lg font-medium">No dashboards available</p>
                    <p className="mt-1">Click "Create Dashboard" to add your first dashboard</p>
                  </div>
                </td>
              </tr>
            ) : (
              dashboards.map((dashboard) => (
                <React.Fragment key={dashboard.id}>
                  <tr className="hover:bg-[#003366]/5 cursor-pointer" onClick={() => toggleDashboardExpansion(dashboard.id)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {expandedDashboards[dashboard.id] ? (
                          <ChevronDown className="h-4 w-4 text-[#003366] mr-2" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-[#003366] mr-2" />
                        )}
                        <div className="font-bold text-[#003366]">{dashboard.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2">
                          {dashboard.namespaces.length} namespaces
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mr-2">
                          {dashboard.namespaces.reduce((total, ns) => total + getMetricsCount(ns), 0)} metrics
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {dashboard.namespaces.reduce((total, ns) => total + (ns.resourceNames?.length || 0), 0)} resources
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {(() => {
                        const account = awsAccounts.find((acc) => acc.AccountNo === dashboard.accountNo);
                        return account ? `${account.AccountName} (${account.AccountNo})` : dashboard.accountNo;
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {regions.find((r) => r.name === dashboard.region)?.description || dashboard.region}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Bell className="h-4 w-4 text-orange-500 mr-2" />
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          {calculateTotalAlarms(dashboard)} alarms
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-center space-x-2" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => onEdit(dashboard)}
                          className="flex items-center gap-2 px-3 py-1.5 bg-[#003366] text-white rounded hover:bg-[#004488] transition-colors"
                        >
                          <Edit size={16} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteConfirmation(dashboard.id)}
                          className="text-red-600 hover:text-red-800"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setTooltip({
                              visible: true,
                              content: "Delete",
                              x: rect.left + rect.width / 2,
                              y: rect.bottom + 5,
                            });
                          }}
                          onMouseLeave={() =>
                            setTooltip({ visible: false, content: "", x: 0, y: 0 })
                          }
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded dashboard detail */}
                  {expandedDashboards[dashboard.id] && (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 bg-gray-50">
                        <div className="ml-6 space-y-4">
                          {dashboard.namespaces.map((namespace, index) => (
                            <div key={index} className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
                              <div
                                className="flex items-center cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleNamespaceExpansion(dashboard.id, namespace.name);
                                }}
                              >
                                {expandedNamespaces[`${dashboard.id}-${namespace.name}`] ? (
                                  <ChevronDown className="h-4 w-4 text-[#003366] mr-2" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-[#003366] mr-2" />
                                )}
                                <div className="flex items-center">
                                  {getIconComponent(namespace.name)}
                                  <span className="font-medium text-[#003366] ml-2">{namespace.name}</span>
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {getMetricsCount(namespace)} metrics
                                  </span>
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {namespace.resourceNames?.length || 0} resources
                                  </span>
                                  {/* Added alarm count for namespace */}
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                    <Bell className="h-3 w-3 mr-1" />
                                    {calculateNamespaceAlarms(namespace)} alarms
                                  </span>
                                </div>
                              </div>

                              {expandedNamespaces[`${dashboard.id}-${namespace.name}`] && (
                                <div className="mt-3 ml-6">
                                  {/* Resource Names Section */}
                                  {namespace.resourceNames && namespace.resourceNames.length > 0 && (
                                    <div className="mb-4">
                                      <div className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                                        <Tag className="h-4 w-4 mr-1" />
                                        Resource Names:
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {namespace.resourceNames.map((resourceName, i) => (
                                          <div
                                            key={i}
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                            onMouseEnter={(e) => {
                                              const rect = e.currentTarget.getBoundingClientRect();
                                              setTooltip({
                                                visible: true,
                                                content: resourceName,
                                                x: rect.left + rect.width / 2,
                                                y: rect.bottom + 5,
                                              });
                                            }}
                                            onMouseLeave={() => setTooltip({ visible: false, content: "", x: 0, y: 0 })}
                                          >
                                            <span className="truncate max-w-[160px]">{resourceName}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Metrics Section */}
                                  <div>
                                    <div className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                                      <BarChart2 className="h-4 w-4 mr-1" />
                                      Metrics:
                                    </div>
                                    <div className="space-y-2">
                                      {/* Use metricsWithAlarms for display if available, otherwise use metrics */}
                                      {(namespace.metricsWithAlarms || namespace.metrics || []).map((metric, i) => {
                                        // Handle both object format (metricsWithAlarms) and string format (metrics)
                                        const metricName = typeof metric === 'object' ? metric.metricName : metric;
                                        const alarmDetails = typeof metric === 'object' ? metric.alarmDetails : [];

                                        return (
                                          <div key={i} className="bg-gray-50 p-3 rounded border">
                                            <div
                                              className={`flex items-center ${alarmDetails && alarmDetails.length > 0 ? 'cursor-pointer' : ''}`}
                                              onClick={(e) => {
                                                if (alarmDetails && alarmDetails.length > 0) {
                                                  e.stopPropagation();
                                                  toggleMetricExpansion(dashboard.id, namespace.name, metricName);
                                                }
                                              }}
                                            >
                                              {alarmDetails && alarmDetails.length > 0 && (
                                                expandedMetrics[`${dashboard.id}-${namespace.name}-${metricName}`] ? (
                                                  <ChevronDown className="h-3 w-3 text-[#003366] mr-2" />
                                                ) : (
                                                  <ChevronRight className="h-3 w-3 text-[#003366] mr-2" />
                                                )
                                              )}
                                              <span className="font-medium text-blue-800 text-sm">{metricName}</span>
                                              {alarmDetails && alarmDetails.length > 0 && (
                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                  <Bell className="h-3 w-3 mr-1" />
                                                  {alarmDetails.length} alarms
                                                </span>
                                              )}
                                            </div>

                                            {/* Alarm Details Table */}
                                            {expandedMetrics[`${dashboard.id}-${namespace.name}-${metricName}`] &&
                                              alarmDetails && alarmDetails.length > 0 && (
                                                <div className="mt-3 ml-5">
                                                  <div className="text-xs font-medium text-gray-600 mb-2 flex items-center">
                                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                                    Alarm Details:
                                                  </div>
                                                  <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                                                      <thead className="bg-gray-100">
                                                        <tr>
                                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Alarm Name
                                                          </th>
                                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Resource Name
                                                          </th>
                                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Statistic
                                                          </th>
                                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Threshold
                                                          </th>
                                                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Comparison
                                                          </th>
                                                        </tr>
                                                      </thead>
                                                      <tbody className="bg-white divide-y divide-gray-200">
                                                        {alarmDetails.map((alarm, alarmIndex) => (
                                                          <tr key={alarmIndex} className="hover:bg-gray-50">
                                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                              <span
                                                                className="truncate max-w-[120px] block cursor-pointer"
                                                                onMouseEnter={(e) => {
                                                                  const rect = e.currentTarget.getBoundingClientRect();
                                                                  setTooltip({
                                                                    visible: true,
                                                                    content: alarm.alarmName,
                                                                    x: rect.left + rect.width / 2,
                                                                    y: rect.bottom + 5,
                                                                  });
                                                                }}
                                                                onMouseLeave={() => setTooltip({ visible: false, content: "", x: 0, y: 0 })}
                                                              >
                                                                {alarm.alarmName}
                                                              </span>
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                              <span
                                                                className="truncate max-w-[120px] block cursor-pointer"
                                                                onMouseEnter={(e) => {
                                                                  const rect = e.currentTarget.getBoundingClientRect();
                                                                  setTooltip({
                                                                    visible: true,
                                                                    content: alarm.resourceName,
                                                                    x: rect.left + rect.width / 2,
                                                                    y: rect.bottom + 5,
                                                                  });
                                                                }}
                                                                onMouseLeave={() => setTooltip({ visible: false, content: "", x: 0, y: 0 })}
                                                              >
                                                                {alarm.resourceName}
                                                              </span>
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                              {alarm.statistic}
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                              {alarm.threshold}
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900">
                                                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${alarm.comparisonOperator.includes('Greater')
                                                                  ? 'bg-red-100 text-red-800'
                                                                  : 'bg-yellow-100 text-yellow-800'
                                                                }`}>
                                                                {alarm.comparisonOperator}
                                                              </span>
                                                            </td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              )}

                                            {/* No alarms message */}
                                            {expandedMetrics[`${dashboard.id}-${namespace.name}-${metricName}`] &&
                                              (!alarmDetails || alarmDetails.length === 0) && (
                                                <div className="mt-3 ml-5 text-xs text-gray-500 italic">
                                                  No alarms configured for this metric
                                                </div>
                                              )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {tooltip.visible && (
        <div
          style={{
            position: "fixed",
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            transform: "translateX(-50%)",
            zIndex: 1000,
          }}
          className="bg-black/80 text-white text-xs rounded px-2 py-1 shadow-md"
        >
          {tooltip.content || "Delete"}
        </div>
      )}

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: "8px",
            padding: "8px",
            maxWidth: "450px",
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          style={{
            padding: "16px 24px",
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#003366",
          }}
        >
          {"Confirm Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{
              color: "#4a4a4a",
              fontSize: "1rem",
              marginBottom: "8px",
            }}
          >
            Are you sure you want to delete this dashboard?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
          <button
            onClick={() => setOpenDialog(false)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#FFD700",
              color: "#003366",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#FFE44D")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#FFD700")
            }
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#003366",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
              transition: "background-color 0.2s",
            }}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "#004488")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "#003366")
            }
            autoFocus
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Save size={16} />
            )}
            Confirm
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}